// MJOS-147 — MyJarvis agent panel.
//
// Controlled component (mutex pattern). Layout.tsx owns the
// rightPanel: "voice" | "agent" | null state and passes:
//   - <VoicePanel  open={rightPanel === "voice"}  onClose={() => setRightPanel(null)} />
//   - <AgentPanel  open={rightPanel === "agent"}  onClose={() => setRightPanel(null)} />
// The two panels share the same physical right-side slot; only one is
// open at a time. Both animate width 0 → their content width with the
// same `transition-[width]` pattern VoicePanel already uses.
//
// AgentPanelToggle is a small chip button mirroring VoicePanelToggle.
// Layout renders both toggles in the top-right toggle area and hides
// whichever one is currently active.
//
// The chat body is assistant-ui (@assistant-ui/react) driven by a
// pi-agent-core Agent — see MyJarvisRuntimeProvider + Thread. Authentication
// for proxy calls is handled by a global fetch interceptor installed in
// agent-runtime.ts that swaps in a fresh WorkOS access token.
import { useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { MessageCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { MyJarvisRuntimeProvider } from "./MyJarvisRuntimeProvider";
import { Thread } from "./thread";

/**
 * Small toggle chip that opens the agent panel. Sized + styled to sit
 * next to VoicePanelToggle in the top-right cluster.
 */
export function AgentPanelToggle({ onClick }: { onClick: () => void }) {
	return (
		<button
			type="button"
			aria-label="Open MyJarvis agent"
			onClick={onClick}
			title="MyJarvis Agent"
			className="relative flex h-9 items-center gap-1.5 rounded-lg border bg-background px-2.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
		>
			<MessageCircle className="h-[18px] w-[18px]" />
			<span className="text-xs font-medium">MyJarvis</span>
		</button>
	);
}

/**
 * The right-side agent panel itself. Controlled by `open` from Layout.
 * Always mounted so the chat keeps its state between open/close cycles —
 * only the width animates. Same architecture as VoicePanel.
 */
export function AgentPanel({
	open,
	onClose,
}: {
	open: boolean;
	onClose: () => void;
}) {
	const { user, getAccessToken } = useAuth();

	const getToken = useCallback(
		async () => (await getAccessToken()) || undefined,
		[getAccessToken],
	);

	const userEmail = (user as { email?: string } | null)?.email;

	return (
		<aside
			// `inert` (React 19) when closed: removes the subtree from the tab
			// order AND hides it from assistive tech, and crucially blurs any
			// focused descendant (the composer textarea) — which is what the
			// bare `aria-hidden` could not do (aria-hidden on a focused
			// ancestor is invalid and the browser blocks it).
			inert={!open}
			className={cn(
				"shrink-0 overflow-hidden border-l bg-background transition-[width] duration-200 ease-out",
				open ? "w-[420px]" : "w-0",
			)}
		>
			{/* Inner content is fixed-width so it doesn't visually collapse
          during the width animation — the parent's overflow:hidden
          reveals it gradually as the parent grows. */}
			<div className="flex h-full w-[420px] flex-col">
				<div className="flex items-center justify-between border-b px-3 py-2">
					<span className="text-sm font-semibold">MyJarvis · Kimi K2.6</span>
					<button
						type="button"
						onClick={onClose}
						className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
						title="Close"
						aria-label="Close MyJarvis agent"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
				<div className="relative flex-1 overflow-hidden">
					{user ? (
						<MyJarvisRuntimeProvider getAccessToken={getToken} userEmail={userEmail}>
							<Thread />
						</MyJarvisRuntimeProvider>
					) : (
						<div className="absolute inset-x-4 top-4 rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
							Sign in to use the MyJarvis agent.
						</div>
					)}
				</div>
			</div>
		</aside>
	);
}
